import { TCFSnapshot } from '../privacy';

/**
 * Consent block carried by every Mosaic (Permutive-shaped) event (DAP-114771).
 *
 * Mosaic follows the HBO Max / Discovery+ model from PRD 068 (Optimized Consent
 * Application): capture the event, then apply consent and opt-outs downstream in
 * the C360 ID Graph rather than hard-gating capture on the client. That only
 * works if the consent state travels with the event, which is what this block
 * is for.
 *
 * The block has two layers:
 *   - `consents`, the WBD category consents (`tc`, `ftc`, `pfc`) in the same
 *     shape Max/D+ and the iSDK use, so downstream can filter these events with
 *     the consent logic it already has. This is the decision layer, and every
 *     category is always present, defaulting to not-consented.
 *   - `tcf_string`, the raw TC string the categories were derived from. Category
 *     consents are lossy — purposes 9 and 10 map to no category at all — and the
 *     mapping is subject to Privacy Legal review, so carrying the source signal
 *     keeps the derivation auditable and lets downstream re-derive any grant
 *     without us shipping a decoded copy of what the string already contains.
 *
 * Whether GDPR applies is deliberately NOT reported. `EMEA_LOCATIONS`, which
 * gates emission, is a sales region — roughly 100 of its 135 countries have no
 * GDPR-equivalent regime and therefore no CMP — so answering that question in the
 * client would mean shipping a jurisdiction list, keeping it current through SDK
 * releases as the law moves, and trusting a client-resolved country that is being
 * migrated to server-side population anyway. The backend decides instead, from
 * `tcf_string` plus the country the receive service resolves. A null `tcf_string`
 * is the signal that no consent mechanism was reached at all.
 *
 * Only the TCF CMP (`window.__tcfapi`) is represented. Mosaic only emits in EMEA
 * (see `Psm.mosaicEnabled`), so there is no US Privacy/CCPA string here — add one
 * if that ever changes. Where a brand resolves consent through UCL or OneTrust
 * instead, `tcf_string` is null and no category is granted.
 */
/**
 * TCF purposes required for each WBD consent category, per the Privacy
 * Legal-approved mapping in "TCF Scenario Mapping" (CAF/605258647):
 *
 *   ftc - 1P Targeting                  <- purposes 1, 2
 *   tc  - 3P Targeting                  <- purposes 1, 3, 4
 *   pfc - 3P Performance & Functional   <- purposes 1, 7
 *
 * Every purpose mapped to a category must be consented for that category to
 * count as consented, which is why withholding purpose 1 alone opts the user out
 * of all three. Purposes 9 and 10 map to no category.
 */
export const TCF_PURPOSES_BY_CATEGORY: Record<string, number[]> = {
  ftc: [1, 2],
  tc: [1, 3, 4],
  pfc: [1, 7],
};

/**
 * A single category consent in the shape Max/D+ and the iSDK use (ESD 056:
 * Identity Consents). Keys are deliberately camelCase, unlike the snake_case
 * Permutive attributes around them, so a downstream consumer can reuse its
 * existing consent filter against these records unchanged.
 */
export type CategoryConsent = {
  categoryId: string;
  hasConsented: boolean;
};

/**
 * Derive the WBD category consents from the TCF purpose grants.
 *
 * A category is consented only if every purpose it maps to was granted, so a
 * silent or absent CMP yields `false` throughout — including in the ~100 EMEA
 * countries with no GDPR regime, where no CMP is deployed at all. The client
 * never infers consent from the absence of a consent mechanism; that matches the
 * iSDK global context, where each `user.consents` flag "defaults to false if not
 * provided", and the legally safe reading that absent consent is not consent.
 *
 * Whether a `false` here means "the user refused" or "consent was never
 * required" is for the backend to decide from `tcf_string` and the country it
 * resolves. That determination is deliberately not made in the SDK.
 *
 * Only explicit consent counts. Legitimate interest is deliberately excluded,
 * because the approved mapping is expressed in terms of the purposes a user
 * accepts. It remains recoverable from `tcf_string` if Legal decides otherwise.
 */
export function buildCategoryConsents(tcf?: TCFSnapshot | null): CategoryConsent[] {
  return Object.entries(TCF_PURPOSES_BY_CATEGORY).map(([categoryId, purposes]) => ({
    categoryId,
    hasConsented: purposes.every((purpose) => tcf?.purposeConsents[String(purpose)] === true),
  }));
}

export type PermutiveConsent = {
  /**
   * WBD category consents (`tc`, `ftc`, `pfc`) derived from the TCF purposes.
   * Always populated, defaulting to not-consented. This is the field downstream
   * filters on; the TCF detail below is the auditable source it was derived from.
   */
  consents: CategoryConsent[];
  /**
   * Raw TC string, or `null` when no CMP responded. Downstream can re-derive the
   * full grant from it, and its absence is the signal that no consent mechanism
   * was reached at all.
   */
  tcf_string: string | null;
};

/**
 * Assemble the consent block from PRISM's already-resolved consent state.
 *
 * Reads the TCF snapshot captured during PRISM's own consent check rather than
 * calling `__tcfapi` again, so every event in a pageview reports the same state
 * and no event is delayed on a CMP round trip.
 */
export function buildConsentBlock(args: { tcf?: TCFSnapshot | null }): PermutiveConsent {
  const { tcf } = args;
  return {
    consents: buildCategoryConsents(tcf),
    tcf_string: tcf?.tcString || null,
  };
}
